/**
 * Health Plans utils
 */

import store from '../../../store'
import CoreEnums from '../../../core/utils/enums'
import coreReducer from '../../../core/utils/reducer'

/**
 * Format a date for display
 * @param {Date|string} dateString - Date to format
 * @param {string} format - Format string (default: 'MM/DD/YYYY')
 * @returns {string} Formatted date string
 */
export const formatDate = (dateString, format = 'MM/DD/YYYY') => {
  if (!dateString) return ''

  const date = dateString instanceof Date ? dateString : new Date(dateString)

  if (isNaN(date.getTime())) {
    return ''
  }

  // Simple formatting for now
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  const year = date.getFullYear()

  return `${month}/${day}/${year}`
}

/**
 * Read care plan templates from the backend
 * This is a mock implementation that returns fake data
 * @param {Object} filters - Filter criteria like planType
 * @returns {Promise<Array>} Promise resolving to an array of care plan templates
 */
export const readCarePlanTemplates = async (filters = {}) => {
  // This is a stub function - in a real app, this would call an API
  return new Promise((resolve) => {
    setTimeout(() => {
      const mockData = [
        {
          _id: '1',
          name: 'Diabetes Care Plan',
          description: 'Comprehensive care plan for managing diabetes',
          planType: 'care',
          status: 'active',
          createdAt: '2023-01-15T00:00:00.000Z',
          duration: 12,
          durationType: 'months'
        },
        {
          _id: '2',
          name: 'Hypertension Care Plan',
          description: 'Monitoring and management plan for hypertension',
          planType: 'care',
          status: 'active',
          createdAt: '2023-02-10T00:00:00.000Z',
          duration: 6,
          durationType: 'months'
        },
        {
          _id: '3',
          name: 'Asthma Management',
          description: 'Comprehensive asthma management and monitoring',
          planType: 'care',
          status: 'draft',
          createdAt: '2023-03-05T00:00:00.000Z',
          duration: 12,
          durationType: 'months'
        },
        {
          _id: '4',
          name: 'Holistic Wellness Program',
          description: 'A complete wellness program focusing on nutrition, exercise, and mental health',
          planType: 'wellness',
          status: 'active',
          createdAt: '2023-04-15T00:00:00.000Z',
          duration: 12,
          durationType: 'months'
        },
        {
          _id: '5',
          name: 'Executive Wellness Plan',
          description: 'Wellness program designed for busy professionals',
          planType: 'wellness',
          status: 'active',
          createdAt: '2023-05-10T00:00:00.000Z',
          duration: 6,
          durationType: 'months'
        }
      ]

      // Filter by planType if provided
      let results = mockData
      if (filters.planType) {
        results = mockData.filter((item) => item.planType === filters.planType)
      }

      resolve(results)
    }, 500)
  })
}

/**
 * Create a new care plan template
 * @param {Object} templateData - Template data to create
 * @returns {Promise<Object>} Promise resolving to the created template
 */
export const createCarePlanTemplate = async (templateData) => {
  // This is a stub function - in a real app, this would call an API
  return new Promise((resolve) => {
    setTimeout(() => {
      resolve({
        ...templateData,
        id: Math.random().toString(36).substring(2, 15),
        createdAt: new Date().toISOString()
      })
    }, 500)
  })
}

/**
 * Update an existing care plan template
 * @param {string} templateId - ID of the template to update
 * @param {Object} templateData - Updated template data
 * @returns {Promise<Object>} Promise resolving to the updated template
 */
export const updateCarePlanTemplate = async (templateId, templateData) => {
  // This is a stub function - in a real app, this would call an API
  return new Promise((resolve) => {
    setTimeout(() => {
      resolve({
        ...templateData,
        id: templateId,
        updatedAt: new Date().toISOString()
      })
    }, 500)
  })
}

/**
 * Delete a care plan template
 * @param {string} templateId - ID of the template to delete
 * @returns {Promise<boolean>} Promise resolving to true if deletion was successful
 */
export const deleteCarePlanTemplate = async (templateId) => {
  // This is a stub function - in a real app, this would call an API
  return new Promise((resolve) => {
    setTimeout(() => {
      resolve(true)
    }, 500)
  })
}

/**
 * Calculate plan status based on various factors
 * @param {Object} plan - Plan object
 * @returns {string} Status string (Active, Completed, etc)
 */
export const calculatePlanStatus = (plan) => {
  if (!plan) return 'Unknown'

  const today = new Date()
  const startDate = new Date(plan.startDate)
  const endDate = plan.endDate ? new Date(plan.endDate) : null

  if (plan.status === 'completed') return 'Completed'
  if (plan.status === 'cancelled') return 'Cancelled'
  if (plan.status === 'draft') return 'Draft'

  if (startDate > today) return 'Scheduled'
  if (endDate && endDate < today) return 'Expired'

  return 'Active'
}

/**
 * Get plan duration label
 * @param {number} duration - Duration value
 * @param {string} durationType - Duration type (days, weeks, months, etc)
 * @returns {string} Formatted duration string
 */
export const getPlanDurationLabel = (duration, durationType) => {
  if (!duration || !durationType) return ''

  const type = durationType.toLowerCase()
  const value = parseInt(duration, 10)

  if (isNaN(value)) return ''

  if (value === 1) {
    // Singular form
    return `1 ${type.substring(0, type.length - 1)}`
  }

  return `${value} ${type}`
}

/**
 * Open a plan in a new tab
 * @param {string} planType - Type of plan
 * @param {string} planId - ID of the plan
 */
export const openPlanInTab = (planType, planId) => {
  const tabKey = getPlanTabKey(planType)
  store.dispatch(
    coreReducer.actions.updateStateAction({
      name: tabKey,
      action: {
        action: CoreEnums.tabActions.CHANGE,
        key: tabKey,
        planId
      }
    })
  )
}

/**
 * Get the tab key for a plan type
 * @param {string} planType - Type of plan
 * @returns {string} Tab key
 */
const getPlanTabKey = (planType) => {
  switch (planType) {
    case 'preventive':
      return CoreEnums.tabKeys.PREVENTATIVE_PLANS
    case 'care':
      return CoreEnums.tabKeys.CARE_PLANS
    case 'treatment':
      return CoreEnums.tabKeys.TREATMENT_PLANS
    case 'rehabilitation':
      return CoreEnums.tabKeys.REHABILITATION_PLANS
    case 'wellness':
      return CoreEnums.tabKeys.WELLNESS_PLANS
    case 'nutrition':
      return CoreEnums.tabKeys.MEAL_PLANS
    default:
      return CoreEnums.tabKeys.CARE_PLANS
  }
}

export default {
  formatDate,
  readCarePlanTemplates,
  createCarePlanTemplate,
  updateCarePlanTemplate,
  deleteCarePlanTemplate,
  calculatePlanStatus,
  getPlanDurationLabel,
  openPlanInTab
}
